import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	clearLocalStorage,
	clearToken,
	getLocalStorage,
	setLocalStorage
} from '../utils/localStorage';
import { clearSession } from '../utils/session';
import { setCookie, removeCookie } from '../utils/cookies';

declare global {
	interface Window {
		updateUserName: any;
	}
}
//TODO:(1) Review requiresCreateUser - Its not part of the accountAPI.login

export interface UserProfile {
	firstName: string;
	lastName: string;
	email: string;
	userId: string;
	signInType: string;
	requiresCreateUser: boolean;
	isUserLoggedIn: boolean;
	emailVerified: boolean;
	requiresEmailVerification: boolean;
	requiresPersonalization: boolean;
}

export interface Account {
	user: any;
	shippingAddress: any;
}

export interface UserSliceState {
	userProfile?: UserProfile;
	account: Account;
	termsOfServiceStatus: string;
	advertisingTargetingStatus: string;
	smsConsentAccepted: string;
	emailOptin: boolean | null;
}

const initialState: UserSliceState = {
	userProfile: {
		firstName: '',
		lastName: '',
		email: '',
		userId: '',
		signInType: '',
		requiresCreateUser: false,
		isUserLoggedIn: false,
		emailVerified: false,
		requiresEmailVerification: false,
		requiresPersonalization: false
	},
	account: {
		user: null,
		shippingAddress: null
	},
	termsOfServiceStatus: '',
	advertisingTargetingStatus: '',
	smsConsentAccepted: '',
	emailOptin: null
};

const userInfo = getLocalStorage('userProfile');
if (userInfo) {
	initialState.userProfile = userInfo;
}

const tosStatus = getLocalStorage('termsOfServiceStatus');
if (tosStatus) {
	initialState.termsOfServiceStatus = tosStatus;
}

export const userSlice = createSlice({
	name: 'user',
	initialState: initialState,
	reducers: {
		setUserProfile: (state: UserSliceState, action) => {
			const { userProfile, user } = action.payload;

			setLocalStorage('userProfile', userProfile);
			setCookie('userProfile', JSON.stringify(userProfile), 90);

			state.userProfile = userProfile;
			state.account = { ...state.account, user: user };

			if (window.updateUserName) {
				window.updateUserName();
			}
		},
		setTermsOfServiceStatus: (state: UserSliceState, action: PayloadAction<string>) => {
			setLocalStorage('termsOfServiceStatus', action.payload);
			state.termsOfServiceStatus = action.payload;
		},
		setAdvertisingTargetingStatus: (state: UserSliceState, action: PayloadAction<string>) => {
			state.advertisingTargetingStatus = action.payload;
		},
		setSMSMessaging: (state: UserSliceState, action: PayloadAction<string>) => {
			state.smsConsentAccepted = action.payload;
		},
		setEmailPreferenceStatus: (state: UserSliceState, action: any) => {
			state.emailOptin = action.payload;
		},
		setAccountInformation: (state: UserSliceState, action: any) => {
			if (action.payload) {
				state.account = action.payload;
			}
		},
		setUser: (state: UserSliceState, action: any) => {
			if (action.payload) {
				state.account = { ...state.account, user: action.payload };
			}
		},
		setShippingAddress: (state: UserSliceState, action: any) => {
			state.account.shippingAddress = action.payload;
		},
		logout: (state: UserSliceState) => {
			const nonLoginCTR: any = document.querySelector('.nonlogin-ctr');
			const noginCTR: any = document.querySelector('.login-ctr');

			if (nonLoginCTR && noginCTR) {
				nonLoginCTR.style.display = 'block';
				noginCTR.style.display = 'none';
			}

			clearToken();
			removeCookie('userProfile');
			removeCookie('cartId'); // Removing saved cart from session
			clearLocalStorage('termsOfServiceStatus');
			clearLocalStorage('userProfile');
			clearSession('consentToken');
			clearLocalStorage('isSMSConsentChecked');
			//TODO:(1) Review requiresCreateUser - Its not part of the accountAPI.login
			clearLocalStorage('isRequiresCreateUser');
			clearLocalStorage('isRequiresPersonalization');
			clearLocalStorage('requiresEmailVerification');

			state.userProfile = {
				firstName: '',
				lastName: '',
				email: '',
				userId: '',
				signInType: '',
				requiresCreateUser: false,
				isUserLoggedIn: false,
				emailVerified: false,
				requiresEmailVerification: false,
				requiresPersonalization: false
			};
			state.account = {
				user: null,
				shippingAddress: null
			};
			state.termsOfServiceStatus = '';
			state.smsConsentAccepted = '';
			state.emailOptin = null;

			if (window.updateUserName) {
				window.updateUserName();
			}
		},
		clearBrowserStorage: () => {
			clearToken();
			removeCookie('userProfile');
			removeCookie('cartId');
			clearLocalStorage('termsOfServiceStatus');
			clearLocalStorage('userProfile');
			clearSession('consentToken');
			clearLocalStorage('isSMSConsentChecked');
			clearLocalStorage('isRequiresCreateUser');
			clearLocalStorage('isRequiresPersonalization');

			//TODO:(1) Review requiresCreateUser - Its not part of the accountAPI.login
			if (window.updateUserName) {
				window.updateUserName();
			}
		}
	}
});

export const {
	setUserProfile,
	setTermsOfServiceStatus,
	setAdvertisingTargetingStatus,
	setSMSMessaging,
	setEmailPreferenceStatus,
	setAccountInformation,
	setUser,
	setShippingAddress,
	logout,
	clearBrowserStorage
} = userSlice.actions;

export default userSlice.reducer;
